import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    Chip,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import styled from 'styled-components';
import { loadAllAgreements, loadCompanyAgreements } from '../../../data/api/agreements/AgreementRoute';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';
import AgreementWizard from './wizard/AgreementWizard';
import CompanyAgreementDesc from './companyAgreements/CompanyAgreementDesc';

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        background: '#fff',
        '& fieldset': { borderColor: 'rgba(15, 34, 77, 0.14)' },
        '&.Mui-focused fieldset': { borderColor: '#d6aa3a' },
    },
};

const STATUS_OPTIONS = [
    'Pendiente de firmas',
    'Firmado',
    'Aprobado',
    'Cancelado',
    'Rechazado',
];

const CompanyAgreements = ({
    user = [],
    loadUser,
    setCountAgreement
}) => {
    const { token, auth } = useIndex();

    const [agreements, setAgreements] = useState([]);
    const [agreementPanel, setAgreementPanel] = useState('');

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [meta, setMeta] = useState(null);

    const [statusFilter, setStatusFilter] = useState('');
    const [companySearch, setCompanySearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const isAdmin = String(auth?.role_id) === String(process.env.REACT_APP_ADMIN_ROLE);

    const handleExpandAgreement = (panel) => (event, isExpanded) => {
        setAgreementPanel(isExpanded ? panel : false);
    };

    const buildParams = (p, pp) => {
        const params = { page: p, per_page: pp };
        if (statusFilter) params.status = statusFilter;
        if (isAdmin && companySearch) params.company = companySearch;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        return params;
    };

    const loadData = async (p = page, pp = perPage) => {
        try {
            let response;

            if (isAdmin) {
                response = await loadAllAgreements(token, buildParams(p, pp));
            } else {
                if (!user?.uuid) return;
                response = await loadCompanyAgreements(token, user.uuid, buildParams(p, pp));
            }

            const { data } = response;

            if (data?.success) {
                setAgreements(data?.data ?? []);
                setMeta(data?.meta ?? null);
                setCountAgreement(data?.meta?.total ?? (data?.data?.length ?? 0));
            }
        } catch (error) {
            validErrors(error);
        }
    };

    useEffect(() => {
        if (!auth?.role_id) return;
        if (!isAdmin && !user?.uuid) return;
        loadData(page, perPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.role_id, user?.uuid, page, perPage, statusFilter, dateFrom, dateTo]);

    useDebounce(() => {
        if (!auth?.role_id) return;
        if (!isAdmin) return;
        if (page !== 1) {
            setPage(1);
        } else {
            loadData(1, perPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, 600, [companySearch]);

    const handleChangePage = (_e, newPage) => setPage(newPage);

    const handleChangePerPage = (e) => {
        setPerPage(+e.target.value);
        setPage(1);
    };

    const handleChangeFilter = (setter) => (e) => {
        setter(e.target.value);
        setPage(1);
    };

    const clearFilters = () => {
        setStatusFilter('');
        setCompanySearch('');
        setDateFrom('');
        setDateTo('');
        setPage(1);
    };

    const reload = () => loadData(page, perPage);

    return (
        <Wrapper>
            {/* ACTION */}
            {(String(user?.role_id) === String(process.env.REACT_APP_ADMIN_ROLE) ||
                String(auth?.role_id) === String(process.env.REACT_APP_ADMIN_ROLE)) && (
                    <ActionCard>
                        <AgreementWizard user={user} loadData={reload} />
                    </ActionCard>
                )}

            {/* FILTERS */}
            <FiltersCard>
                <FiltersGrid $admin={isAdmin}>
                    {isAdmin && (
                        <TextField
                            size="small"
                            placeholder="Buscar empresa por nombre o NIT..."
                            value={companySearch}
                            onChange={(e) => setCompanySearch(e.target.value)}
                            sx={{ ...fieldStyles, gridColumn: '1 / -1' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}

                    <FormControl size="small" sx={fieldStyles}>
                        <InputLabel>Estado</InputLabel>
                        <Select
                            label="Estado"
                            value={statusFilter}
                            onChange={handleChangeFilter(setStatusFilter)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        type="date"
                        label="Desde"
                        InputLabelProps={{ shrink: true }}
                        value={dateFrom}
                        onChange={handleChangeFilter(setDateFrom)}
                        sx={fieldStyles}
                    />

                    <TextField
                        size="small"
                        type="date"
                        label="Hasta"
                        InputLabelProps={{ shrink: true }}
                        value={dateTo}
                        onChange={handleChangeFilter(setDateTo)}
                        sx={fieldStyles}
                    />

                    <FormControl size="small" sx={fieldStyles}>
                        <InputLabel>Por página</InputLabel>
                        <Select
                            label="Por página"
                            value={perPage}
                            onChange={handleChangePerPage}
                        >
                            <MenuItem value={15}>15</MenuItem>
                            <MenuItem value={30}>30</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                </FiltersGrid>

                <Box className="filters-header">
                    <Box className="filters-info">
                        <Chip label={`Total: ${meta?.total ?? 0}`} size="small" />
                        <Chip
                            label={`Página: ${page}/${meta?.last_page ?? 1}`}
                            size="small"
                            variant="outlined"
                        />
                        <Pagination
                            count={meta?.last_page ?? 1}
                            page={page}
                            onChange={handleChangePage}
                            size="small"
                        />
                        <Button size="small" onClick={clearFilters}>
                            Limpiar filtros
                        </Button>
                    </Box>
                </Box>
            </FiltersCard>

            {/* LIST */}
            <ListContainer>
                <StyledDivider>
                    <span>Listado de contratos</span>
                </StyledDivider>

                {agreements?.length > 0 ? (
                    <AgreementsList>
                        {agreements.map((item) => (
                            <CompanyAgreementDesc
                                key={item.uuid}
                                item={item}
                                loadData={reload}
                                loadUser={loadUser}
                                handleExpandAgreement={handleExpandAgreement}
                                agreementPanel={agreementPanel}
                            />
                        ))}
                    </AgreementsList>
                ) : (
                    <EmptyState>
                        <FilterAltOutlinedIcon />

                        <Typography className="empty-title">
                            No hay contratos
                        </Typography>

                        <Typography className="empty-text">
                            No se encontraron contratos con los filtros actuales.
                        </Typography>
                    </EmptyState>
                )}
            </ListContainer>
        </Wrapper>
    );
};

export default CompanyAgreements;

const Wrapper = styled(Box)`
    width: 100%;
`;

const ActionCard = styled.div`
    margin-bottom: 1rem;
    padding: 1rem;
    border-radius: 18px;
    border: 1px solid rgba(214, 170, 58, 0.25);
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    box-shadow: 0 10px 22px rgba(15, 34, 77, 0.06);
`;

const FiltersCard = styled.div`
    padding: 0.6rem;
    border-radius: 14px;
    background: #fff;
    margin-bottom: 0.8rem;
    border: 1px solid rgba(15, 34, 77, 0.08);

    .filters-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 0.55rem;
        gap: 0.5rem;
        flex-wrap: wrap;
    }

    .filters-info {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        flex-wrap: wrap;
    }

    @media (max-width: 700px) {
        .filters-header {
            flex-direction: column;
            align-items: stretch;
        }
    }
`;

const FiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.45rem;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const ListContainer = styled.div`
    margin-top: 1rem;
`;

const StyledDivider = styled.div`
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    color: #10285f;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &::before,
    &::after {
        content: "";
        flex: 1;
        height: 1px;
        background: rgba(15, 34, 77, 0.13);
    }

    span {
        padding: 0.35rem 0.9rem;
        border-radius: 999px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
    }
`;

const AgreementsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
`;

const EmptyState = styled.div`
    min-height: 220px;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #10285f;

    svg {
        font-size: 2.2rem;
        color: #d6aa3a;
    }

    .empty-title {
        font-weight: 900;
        font-size: 1.2rem;
    }

    .empty-text {
        color: rgba(15, 34, 77, 0.62);
        max-width: 320px;
        font-size: 0.9rem;
    }
`;
